import React, { useId } from "react";
import nextscenesEmblem from "../assets/nextscenes-emblem-v1b3.png";

// Workspace-only visual guidance; no persistence or network side effects.
// Existing saved artwork and locked files remain authoritative.
const safe = (value) => String(value ?? "").trim();

// Shorten display text only; never changes a saved Story teaser.
export function compactBackTeaser(value) {
  const text = safe(value).replace(/\s+/g, " ")
    .replace(/([.!?])(?=[A-Za-zÀ-ÿ])/g, "$1 ");
  const limit = 150;
  if (text.length <= limit) return text;
  const candidate = text.slice(0, limit + 1);
  const endings = /[.!?](?=\s|$)/g;
  let lastFullSentence = -1;
  for (const match of candidate.matchAll(endings)) {
    if (match.index >= 56) lastFullSentence = match.index + 1;
  }
  if (lastFullSentence > 0) return text.slice(0, lastFullSentence);
  const lastWord = candidate.lastIndexOf(" ", limit);
  const cut = lastWord >= 70 ? lastWord : limit;
  return `${text.slice(0, cut).replace(/[\s,;:—–-]+$/, "")}…`;
}

export default function NovelWorkingCoverPairV1({
  side = "front", title = "", authorName = "", teaser = "", isFR = false,
}) {
  const back = side === "back";
  const uid = useId().replace(/:/g, "");
  const nightId = `ns-novel-v1b-night-${uid}`;
  const bronzeId = `ns-novel-v1b-bronze-${uid}`;
  const landscapeId = `ns-novel-v1b-landscape-${uid}`;
  const moonId = `ns-novel-v1b-moon-${uid}`;
  const bookTitle = safe(title) || (isFR ? "Votre roman" : "Your Novel");
  const byline = safe(authorName) || (isFR ? "Nom de l'auteur" : "Author name");
  const storyTeaser = compactBackTeaser(teaser);

  return (
    <div
      data-novel-working-cover={back ? "back" : "front"}
      data-novel-working-palette="midnight-bronze"
      aria-label={back
        ? (isFR ? "Proposition de quatrième de couverture" : "Working back-cover design")
        : (isFR ? "Proposition de couverture avant" : "Working front-cover design")}
      style={{
        position: "relative", isolation: "isolate", overflow: "hidden",
        containerType: "inline-size", width: "100%", maxWidth: "100%",
        aspectRatio: "2 / 3", borderRadius: 4,
        background: "#131d32", color: "#f7e9d4",
        fontFamily: "Georgia, 'Times New Roman', serif",
        boxShadow: "0 8px 22px rgba(18, 25, 44, 0.23)",
      }}
    >
      <svg viewBox="0 0 600 900" width="100%" height="100%"
        preserveAspectRatio="xMidYMid slice" aria-hidden="true"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
        <defs>
          <linearGradient id={nightId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#293044" />
            <stop offset="42%" stopColor="#151e33" />
            <stop offset="100%" stopColor="#342632" />
          </linearGradient>
          <linearGradient id={bronzeId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#f0d5a7" />
            <stop offset="55%" stopColor="#b98056" />
            <stop offset="100%" stopColor="#e3ba84" />
          </linearGradient>
          <linearGradient id={landscapeId} x1="0" y1="0" x2="0.9" y2="1">
            <stop offset="0%" stopColor="#57627a" />
            <stop offset="50%" stopColor="#344660" />
            <stop offset="100%" stopColor="#151e33" />
          </linearGradient>
          <radialGradient id={moonId} cx="50%" cy="45%" r="55%">
            <stop offset="0%" stopColor="#fff0d0" />
            <stop offset="70%" stopColor="#e9c38e" />
            <stop offset="100%" stopColor="#b98b66" />
          </radialGradient>
        </defs>
        <rect x="0" y="0" width="600" height="900" fill={`url(#${nightId})`} />
        <path d="M-50 625 C120 493 210 478 355 357 S536 241 657 83" fill="none" stroke="#b98056" strokeWidth="1.2" opacity="0.23" />
        <path d="M-65 682 C119 546 236 514 390 395 S560 285 652 160" fill="none" stroke="#e3ba84" strokeWidth="1" opacity="0.22" />
        <rect x="26" y="26" width="548" height="848" rx="2" fill="none" stroke={`url(#${bronzeId})`} strokeWidth="2.5" opacity="0.9" />
        <rect x="40" y="40" width="520" height="820" fill="none" stroke="#c79b77" strokeWidth="0.8" opacity="0.38" />
        <path d="M242 58 H358 M242 842 H358" fill="none" stroke={`url(#${bronzeId})`} strokeWidth="2.8" />
        <path d="M288 62 L300 74 L312 62 L300 50Z" fill="#d3aa7b" />
        <g fill="#e7cca3" opacity="0.7">
          <circle cx="105" cy="121" r="2.2" /><circle cx="496" cy="147" r="2" />
          <circle cx="142" cy="310" r="1.4" /><circle cx="493" cy="300" r="1.6" />
          <circle cx="88" cy="476" r="1.4" /><circle cx="525" cy="483" r="1.2" />
        </g>
        {back ? (
          <g fill="none" stroke={`url(#${bronzeId})`} opacity="0.4">
            <path d="M75 790 V615 Q75 488 300 488 Q525 488 525 615 V790" strokeWidth="1.7" />
            <path d="M100 790 V632 Q100 523 300 523 Q500 523 500 632 V790" strokeWidth="1" />
            <path d="M190 770 Q300 664 410 770" strokeWidth="0.9" opacity="0.5" />
            <circle cx="300" cy="690" r="111" strokeWidth="0.7" opacity="0.45" />
          </g>
        ) : (
          <>
            {/* Layered copper arch and landscape: evocative but not genre-specific. */}
            <path d="M69 900 V579 C69 411 171 349 300 349 C429 349 531 411 531 579 V900Z"
              fill="#1a263b" stroke={`url(#${bronzeId})`} strokeWidth="5" />
            <path d="M94 900 V591 C94 436 182 380 300 380 C418 380 506 436 506 591 V900Z"
              fill={`url(#${landscapeId})`} stroke="#ddb183" strokeWidth="1.6" />
            <circle cx="305" cy="553" r="95" fill={`url(#${moonId})`} opacity="0.94" />
            <path d="M94 729 L171 659 L224 702 L290 627 L358 686 L420 615 L506 709 V900 H94Z" fill="#4a536b" />
            <path d="M94 784 L181 719 L265 748 L343 683 L417 732 L506 687 V900 H94Z" fill="#303f58" />
            <path d="M94 822 L191 768 L264 790 L345 748 L428 787 L506 761 V900 H94Z" fill="#1d2d46" />
            <path d="M94 864 Q202 808 292 833 Q383 856 506 818 V900 H94Z" fill="#152339" />
            <path d="M251 900 C255 863 288 842 324 820 Q357 801 366 778"
              fill="none" stroke="#e8c394" strokeWidth="4" opacity="0.8" />
            <path d="M94 900 V591 C94 436 182 380 300 380 C418 380 506 436 506 591 V900"
              fill="none" stroke="#b98c6c" strokeWidth="3" opacity="0.42" />
            <g fill="none" stroke="#d4ab7a" strokeWidth="2" opacity="0.68">
              <path d="M122 797 Q142 767 139 718 Q136 680 115 656" />
              <path d="M477 804 Q454 771 462 722 Q464 687 487 664" />
              <path d="M140 735 Q111 726 110 698 M139 704 Q158 698 161 672" />
              <path d="M461 741 Q488 730 486 701 M462 700 Q443 695 440 674" />
            </g>
            {/* A subtle open-book signature beneath the landscape. */}
            <path d="M195 851 Q248 837 300 857 Q351 837 405 851 L405 867 Q346 853 300 875 Q252 854 195 867Z"
              fill="#c69a73" opacity="0.43" />
          </>
        )}
      </svg>
      {back ? (
        <>
          <div style={{ position: "absolute", left: "13%", right: "13%", top: "11%",
            display: "flex", flexDirection: "column", alignItems: "stretch", gap: "12px" }}>
            <div style={{ fontFamily: "Arial, sans-serif", letterSpacing: "0.18em",
              fontWeight: 700, fontSize: "clamp(7px, 3.6cqw, 10px)",
              textAlign: "center", color: "#e8c99d" }}>
              {isFR ? "UN RÉCIT À DÉCOUVRIR" : "A STORY TO DISCOVER"}
            </div>
            <div style={{ width: "34%", margin: "0 auto", borderBottom: "1px solid #c49772" }} />
            {storyTeaser ? (
              <div data-novel-working-back-teaser="true" style={{
                fontSize: "clamp(8px, 5cqw, 13px)", lineHeight: 1.42,
                textAlign: "justify", textAlignLast: "left", textJustify: "inter-word", hyphens: "none",
                maxHeight: "48cqw", overflow: "hidden", display: "-webkit-box",
                WebkitBoxOrient: "vertical", WebkitLineClamp: 8,
                whiteSpace: "normal", overflowWrap: "break-word",
                color: "#f8eddb", textShadow: "0 1px 2px #101628",
              }}>
                {storyTeaser}
              </div>
            ) : (
              <div data-novel-working-back-empty="true" style={{
                paddingTop: "12%", fontSize: "clamp(12px, 8cqw, 20px)",
                lineHeight: 1.4, fontStyle: "italic", textAlign: "center",
                color: "#f7dfbc",
              }}>
                {isFR ? "Chaque histoire mérite d'être racontée." : "Every story deserves to be told."}
              </div>
            )}
          </div>
          <div style={{ position: "absolute", left: "12%", right: "11%", bottom: "8%",
            height: "14%", display: "flex", alignItems: "flex-end",
            justifyContent: "space-between", gap: "8%" }}>
            <div data-novel-working-brand-mark="true" style={{
              width: "51%", height: "100%", display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "flex-end", gap: "4%",
            }}>
              <div style={{ width: "40%", maxWidth: "38px", aspectRatio: "1",
                display: "flex", justifyContent: "center", alignItems: "center",
                borderRadius: "50%", border: "1px solid #bf9a70",
                background: "#e9d8bd", padding: "5%", boxSizing: "border-box" }}>
                <img src={nextscenesEmblem} alt="NextScenes logo"
                  style={{ display: "block", width: "100%", height: "100%",
                    objectFit: "contain" }} />
              </div>
              <div style={{ fontFamily: "Arial, sans-serif", letterSpacing: "0.11em",
                fontSize: "clamp(6px, 3.5cqw, 10px)", color: "#efcda0" }}>
                NEXTSCENES
              </div>
            </div>
            <div data-novel-working-barcode-reserve="true"
              aria-label={isFR ? "Emplacement réservé au code-barres" : "Reserved barcode area"}
              style={{ width: "43%", height: "79%", flexShrink: 0,
                display: "flex", alignItems: "flex-end", justifyContent: "center",
                background: "#e8ddd0", border: "1px solid #bfa88d",
                borderRadius: 2, boxSizing: "border-box", paddingBottom: "8%",
                color: "#706453", fontFamily: "Arial, sans-serif",
                fontSize: "clamp(5px, 2.5cqw, 8px)", letterSpacing: "0.04em",
                textAlign: "center" }}>
              {isFR ? "ZONE CODE-BARRES / ISBN" : "BARCODE / ISBN AREA"}
            </div>
          </div>
        </>
      ) : (
        <div style={{ position: "absolute", inset: "10% 11% 9%",
          display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
          <div style={{ fontFamily: "Arial, sans-serif", letterSpacing: "0.24em",
            fontSize: "clamp(7px, 4cqw, 11px)", fontWeight: 700,
            color: "#dfc29d" }}>
            {isFR ? "ROMAN · RÉCIT" : "NOVEL · STORY"}
          </div>
          <div style={{ marginTop: "10%", maxWidth: "100%", fontWeight: 500,
            lineHeight: 1.08, fontSize: "clamp(12px, 13cqw, 34px)",
            letterSpacing: "0.02em", overflowWrap: "anywhere",
            maxHeight: "34%", overflow: "hidden", color: "#fff1d7",
            textShadow: "0 2px 6px #11192b" }}>
            {bookTitle}
          </div>
          <div style={{ width: "24%", borderTop: "2px solid #e2b88c", marginTop: "7%" }} />
          <div style={{ marginTop: "auto", maxWidth: "100%",
            fontSize: "clamp(10px, 7cqw, 17px)", lineHeight: 1.25,
            letterSpacing: "0.04em", overflowWrap: "anywhere",
            textShadow: "0 2px 5px #11192b" }}>
            {byline}
          </div>
        </div>
      )}
    </div>
  );
}
